import RelationshipGroupView from "../components/common/relationship-view/RelationshipGroupView";
import { Config, ApiEndpoint } from "../types";

// Type for relationship entities from the backend
interface RelationshipEntity {
  id: number | string; // Support both Django IDs and Fulcrum UUIDs
  name?: string;
  entity_name?: string;
  rel_description?: string;
  thumbnail?: string;
  media_type?: string;
  [key: string]: any;
}

/**
 * Unified helper to find an API endpoint by key.
 * Checks tableName, returnedRelationshipsKey, and legacy relationshipKey.
 */
export const findEndpointByKey = (endpoints: ApiEndpoint[], key: string): ApiEndpoint | undefined => {
  if (!endpoints || !key) return undefined;
  return endpoints.find(
    (ep) =>
      ep.tableName === key ||
      ep.returnedRelationshipsKey === key ||
      ep.relationshipKey === key
  );
};


/**
 * Safely extract entity name from relationship entity, handling backend inconsistencies
 * Backend sometimes provides 'name', sometimes 'entity_name'
 */
export const getEntityName = (entity: RelationshipEntity): string => {
  return entity.name || entity.entity_name || `Item ${entity.id}`;
};

/**
 * Extract relationship entities from both geo_entities and related_entities
 * Backend inconsistently stores geo entities in either location
 */
export const extractRelationshipEntities = (relationships: any, tableSlug: string, fallbackRelKey?: string): any[] => {
  if (!relationships) return [];

  // V2: byTableSlug shape — keyed by tableName
  if (relationships.byTableSlug) {
    return relationships.byTableSlug[tableSlug] || [];
  }

  // V1: bucket shape — check geo_entities then related_entities
  const fromGeo =
    relationships.geo_entities?.[tableSlug] || (fallbackRelKey && relationships?.geo_entities?.[fallbackRelKey]) || [];
  if (fromGeo?.length) {
    return fromGeo;
  }

  return (
    relationships.related_entities?.[tableSlug] ||
    (fallbackRelKey && relationships?.related_entities?.[fallbackRelKey]) ||
    []
  );
};

/**
 * Convert relationships to flat array of display items
 * Handles both geo_entities and related_entities, excludes media
 */
export const flattenRelationshipsForDisplay = (
  relationships: any,
  config: Config
): Array<{
  key: string;
  id: string | number;
  name: string;
  isGeo: boolean;
  category: string;
}> => {
  if (!relationships) return [];

  const allItems: Array<{
    key: string;
    id: string | number;
    name: string;
    isGeo: boolean;
    category: string;
  }> = [];

  // V2: Handle byTableSlug shape
  if (relationships.byTableSlug) {
    Object.entries(relationships.byTableSlug).forEach(([slug, items]: [string, any]) => {
      if (!Array.isArray(items)) return;
      const matchingEndpoint = findEndpointByKey(config.apiEndpoints, slug);
      items.forEach((entity: RelationshipEntity) => {
        allItems.push({
          key: slug,
          id: entity.id,
          name: getEntityName(entity),
          isGeo: matchingEndpoint?.entityType === "geo" || !!(matchingEndpoint?.spatialType && matchingEndpoint.spatialType !== "none"),
          category: slug
        });
      });
    });

    if (Array.isArray(relationships.media)) {
      relationships.media.forEach((entity: RelationshipEntity) => {
        allItems.push({
          key: "media",
          id: entity.id,
          name: getEntityName(entity),
          isGeo: false,
          category: "media"
        });
      });
    }

    return allItems;
  }

  // V1 fallback: Process all relationship categories
  const processEntities = (entities: any, sourceKey: string, sourceType: string) => {
    if (!entities) return;

    Object.entries(entities).forEach(([key, entityList]) => {
      if (!Array.isArray(entityList) || entityList.length === 0) return;

      // Find matching endpoint using unified lookup helper
      const matchingEndpoint = findEndpointByKey(config.apiEndpoints, key);

      entityList.forEach((entity: RelationshipEntity) => {
        allItems.push({
          key,
          id: entity.id,
          name: getEntityName(entity),
          isGeo: matchingEndpoint?.entityType === "geo" || !!(matchingEndpoint?.spatialType && matchingEndpoint.spatialType !== "none"),
          category: key
        });
      });
    });
  };

  // Process both sources
  processEntities(relationships.geo_entities, "geo_entities", "geo");
  processEntities(relationships.related_entities, "related_entities", "related");

  if (Array.isArray(relationships.media)) {
    relationships.media.forEach((entity: RelationshipEntity) => {
      allItems.push({
        key: "media",
        id: entity.id,
        name: getEntityName(entity),
        isGeo: false,
        category: "media"
      });
    });
  }

  return allItems;
};

/**
 * Convert relationships to editable row format for RelationshipEditDialog
 * Maps relationships to editable row format, keyed by tableName
 */
export const relationshipsToEditableRow = (relationships: any, config: Config): Record<string, (number | string)[]> => {
  const row: Record<string, (number | string)[]> = {};
  if (!relationships) return row;

  // Handle normalized shape
  if (relationships.byTableSlug) {
    Object.entries(relationships.byTableSlug).forEach(([slug, items]: [string, any]) => {
      row[slug] = items.map((item: any) => item.id);
    });
    if (relationships.media) {
      row["media"] = relationships.media.map((item: any) => item.id);
    }
    return row;
  }

  // Fallback for raw shape
  const processEntities = (entities: any, sourceType: string) => {
    if (!entities) return;

    Object.entries(entities).forEach(([relationshipKey, entityList]) => {
      if (!Array.isArray(entityList) || entityList.length === 0) return;

      // Find matching endpoint using unified lookup helper
      const matchingEndpoint = findEndpointByKey(config.apiEndpoints, relationshipKey);

      if (matchingEndpoint) {
        const ids = entityList.map((entity: RelationshipEntity) => {
          if (typeof entity.id === "string") {
            const numericId = parseInt(entity.id, 10);
            return isNaN(numericId) ? entity.id : numericId;
          }
          return entity.id;
        });
        row[matchingEndpoint.tableName] = ids;
      }
    });
  };

  processEntities(relationships.geo_entities, "geo_entities");
  processEntities(relationships.related_entities, "related_entities");

  if (relationships.media && Array.isArray(relationships.media) && relationships.media.length > 0) {
    row["media"] = relationships.media.map((entity: RelationshipEntity) => entity.id);
  }

  return row;
};

/**
 * Extract media items with proper name resolution
 */
export const extractMediaItems = (
  mediaRelationships: RelationshipEntity[]
): Array<{
  id: number | string;
  name: string;
  entity_name: string;
  entity_thumbnail: string;
  entity_description: string;
  is_primary?: boolean;
  relationship_id?: number;
}> => {
  if (!Array.isArray(mediaRelationships)) return [];

  return mediaRelationships.map((media: RelationshipEntity) => {
    const mediaName = getEntityName(media);
    return {
      id: media.id,
      name: mediaName,
      entity_name: mediaName,
      entity_thumbnail: media?.thumbnail || media?.entity_thumbnail || "",
      entity_description: media.rel_description || "",
      media_type: media?.media_type || "",
      is_primary: (media as any)?.is_primary,
      relationship_id: (media as any)?.relationship_id
    };
  });
};

const getRelationCategoryKey = (relationshipKey: string, endpoints: ApiEndpoint[]) => {
  if (relationshipKey === "media") return "media";

  // Find endpoint by canonical key using unified lookup helper
  const endpoint = findEndpointByKey(endpoints, relationshipKey);

  // If endpoint has a spatial type OR is explicitly a geo entityType, it belongs in geo_entities
  if (
    (endpoint?.spatialType && endpoint.spatialType !== "none") ||
    endpoint?.entityType === "geo"
  ) {
    return "geo_entities";
  }

  return "related_entities";
};

/**
 * Builds a normalized `relationships` object for POST/PATCH payloads.
 *
 * This function:
 * - Accepts either a flattened array of relationship objects
 *   OR a nested relationships object.
 * - Flattens nested structures (if needed).
 * - Categorizes relationship keys into:
 *    - `geo_entities`
 *    - `related_entities`
 *    - `media`
 *
 * Categorization Rules:
 * - `"media"` is assigned to the `media` array.
 * - Endpoints with `spatialType` !== "none"
 *   are assigned to `geo_entities`.
 * - All other keys are assigned to `related_entities`.
 *
 * @param {Array<Record<string, any>> | Record<string, any>} relObj
 * The relationship data to normalize. Can be:
 * - An array of objects like: [{ place: [...] }, { trip: [...] }]
 * - A nested relationships object returned from the backend.
 *
 * @returns {{
 *   geo_entities: Record<string, any>;
 *   related_entities: Record<string, any>;
 *   media: any[];
 * }}
 * A structured relationships object ready to be sent in
 * a POST or PATCH API request payload.
 *
 * @example
 * buildRelationshipForPayload([
 *   { place: [{ id: 1 }] },
 *   { trip: [{ id: 2 }] },
 *   { media: [{ id: 3 }] }
 * ]);
 *
 * // Returns:
 * // {
 * //   geo_entities: { place: [{ id: 1 }] },
 * //   related_entities: { trip: [{ id: 2 }] },
 * //   media: [{ id: 3 }]
 * // }
 */
export function buildRelationshipForPayload(
  relObj: Array<Record<string, any>> | Record<string, any>,
  endpoints: ApiEndpoint[]
) {
  if (!relObj) return relObj;

  let relationship = {
    geo_entities: {},
    media: [],
    related_entities: {}
  };

  // build actual relationship
  if (Array.isArray(relObj)) {
    relObj.forEach((relation) => {
      const relationshipName = Object.keys(relation)[0];
      // get the category that relation belongs
      const relationshipCategoryKey = getRelationCategoryKey(relationshipName, endpoints);
      if (relationshipCategoryKey === "media") {
        relationship.media = relation[relationshipName];
      } else {
        // add the relation that belongs to their corresponding category
        // for example `place` & `family area` should belongs to the `geo_entities` category
        // and other should be `related_entities` except `media`
        relationship[relationshipCategoryKey] = {
          ...relationship[relationshipCategoryKey],
          [relationshipName]: relation[relationshipName]
        };
      }
    });

    return relationship;
  }

  // if relationship is an object, try to flatten first and then categorized the relationship
  if (typeof relObj === "object") {
    const flattenRelationship = getFlattenRelationships(relObj);
    return buildRelationshipForPayload(flattenRelationship, endpoints);
  }

  return relationship;
}

/**
 * Recursively flattens a nested relationships object into
 * an array of key-value relationship pairs.
 *
 * This utility is primarily used to normalize backend-style
 * grouped relationships (e.g. `geo_entities`, `related_entities`)
 * into a flat structure that can later be reorganized for
 * POST/PATCH payload construction.
 *
 * @param {Record<string, any>} relationship
 * A nested relationships object. Example:
 *
 * {
 *   geo_entities: {
 *     place: [{ id: 1 }]
 *   },
 *   related_entities: {
 *     trip: [{ id: 2 }]
 *   }
 * }
 *
 * @returns {Array<Record<string, any>>}
 * A flattened array of relationship objects. Example:
 *
 * [
 *   { place: [{ id: 1 }] },
 *   { trip: [{ id: 2 }] }
 * ]
 *
 * @example
 * getFlattenRelationships({
 *   geo_entities: { place: [{ id: 1 }] },
 *   related_entities: { trip: [{ id: 2 }] }
 * });
 *
 * // Returns:
 * // [
 * //   { place: [{ id: 1 }] },
 * //   { trip: [{ id: 2 }] }
 * // ]
 */
export function getFlattenRelationships(relationship: Record<string, any>) {
  const flattenRelationship: Record<string, any>[] = [];

  Object.keys(relationship).forEach((relKey) => {
    const rel = relationship[relKey];
    if (typeof rel === "object" && rel) {
      // directly push the relation if its value is an array
      if (Array.isArray(rel)) {
        flattenRelationship.push({ [relKey]: rel });
      }
      // if relationship is an object that probably either already grouped
      // by respective category, if so that try to reorganized
      // or just continuously flatten further
      const relFlattenVal = getFlattenRelationships(rel);
      if (relFlattenVal) {
        flattenRelationship.push(...relFlattenVal);
      }
    }
  });
  return flattenRelationship;
}
